import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  EXAM_DURATION_SECONDS,
  MAX_ALLOWED_MISTAKES,
  Quiz,
  QuizBank,
  QuizCategory,
  QuizMode,
  DEFAULT_BANK,
  V2_CATEGORIES,
  bankFromCategory,
  quizListPath,
} from '../../../models/quiz';
import { MotionService } from '../../../motion/motion.service';
import { QuizDataService } from '../../../services/quiz-data.service';
import { QuestionLanguageService } from '../../../services/question-language.service';
import { QuizHistoryEntry, QuizResultsService } from '../../../services/quiz-results.service';

const MODE_KEY = 'drivon.quiz.mode';

@Component({
  selector: 'app-quiz-list',
  standalone: false,
  templateUrl: './quiz-list.component.html',
  styleUrls: ['./quiz-list.component.css'],
})
export class QuizListComponent implements OnInit, OnDestroy {
  quizzes: Quiz[] = [];
  history: Record<number, QuizHistoryEntry> = {};
  currentLang = 'am';
  mode: QuizMode = 'exam';
  bank: QuizBank = DEFAULT_BANK;
  loading = true;
  isFallback = false;
  /** Entrance effects play, except when the list is re-created by a language switch. */
  intro = true;
  /** Language the questions are shown in (after falling back to Armenian when needed). */
  questionsLang = 'am';

  readonly examMinutes = EXAM_DURATION_SECONDS / 60;
  readonly maxMistakes = MAX_ALLOWED_MISTAKES;
  readonly categories = V2_CATEGORIES;

  private readonly motion = inject(MotionService);
  private subscription = new Subscription();
  private questionLanguageChange = new BehaviorSubject<void>(undefined);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private quizData: QuizDataService,
    private quizResults: QuizResultsService,
    private questionLanguage: QuestionLanguageService
  ) {}

  ngOnInit() {
    this.mode = readMode();

    // `/quiz` has no parent params, `/:lang/quiz` gets the language from its parent route;
    // `quiz/v2/:category` picks a category bank; `/quiz` and unknown categories open ABC.
    this.subscription.add(
      combineLatest([
        this.route.parent?.params ?? of({} as Record<string, string>),
        this.route.params,
        this.questionLanguageChange,
      ])
        .pipe(
          switchMap(([parentParams, routeParams]) => {
            this.currentLang = parentParams['lang'] || routeParams['lang'] || storedLanguage();
            this.translate.use(this.currentLang);
            const bank = bankFromCategory(routeParams['category']);
            if (!bank) {
              this.router.navigate([this.categoryLink(V2_CATEGORIES[0])], { replaceUrl: true });
              return of(null);
            }
            this.bank = bank;
            this.history = this.quizResults.getHistory(bank);
            this.loading = true;
            return this.quizData.getQuizSet(this.questionLanguage.get(this.currentLang), bank);
          })
        )
        .subscribe((set) => {
          if (!set) return;
          this.quizzes = set.quizzes;
          this.isFallback = set.isFallback;
          this.intro = !this.motion.recentLanguageSwitch();
          this.questionsLang = set.language;
          this.loading = false;
        })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  get questionCount() {
    return this.quizzes[0]?.questions.length ?? 20;
  }

  get passedCount() {
    return this.quizzes.filter((quiz) => this.history[quiz.id]?.passed).length;
  }

  get attemptedCount() {
    return this.quizzes.filter((quiz) => this.history[quiz.id]).length;
  }

  get quizBasePath() {
    return quizListPath(this.currentLang, this.bank);
  }

  get questionLanguages() {
    return this.questionLanguage.available();
  }

  setQuestionLanguage(language: string) {
    this.questionLanguage.set(language);
    this.questionLanguageChange.next();
  }

  categoryLink(category: QuizCategory) {
    return quizListPath(this.currentLang, { edition: 'v2', category });
  }

  isCategory(category: QuizCategory) {
    return this.bank.category === category;
  }

  setMode(mode: QuizMode) {
    this.mode = mode;
    try {
      localStorage.setItem(MODE_KEY, mode);
    } catch {
      // Not critical - the choice just is not remembered.
    }
  }

  queryParams() {
    return this.mode === 'practice' ? { mode: 'practice' } : {};
  }

  /** Opens a random test, preferring ones that have not been passed yet. */
  startRandom() {
    const notPassed = this.quizzes.filter((quiz) => !this.history[quiz.id]?.passed);
    const pool = notPassed.length ? notPassed : this.quizzes;
    if (!pool.length) return;
    const quiz = pool[Math.floor(Math.random() * pool.length)];
    this.router.navigate([this.quizBasePath, quiz.id], { queryParams: this.queryParams() });
  }

  lastResult(quiz: Quiz): QuizHistoryEntry | undefined {
    return this.history[quiz.id];
  }

  trackById(_: number, quiz: Quiz) {
    return quiz.id;
  }
}

function readMode(): QuizMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'practice' ? 'practice' : 'exam';
  } catch {
    return 'exam';
  }
}

function storedLanguage() {
  try {
    return localStorage.getItem('selectedLanguage') || 'am';
  } catch {
    return 'am';
  }
}
